<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useOpsStore } from '@/stores/ops'
import { usePermissionsStore } from '@/stores/permissions'
import { useToastStore } from '@/stores/toast'
import { useCurrentOpRole } from '@/composables/useCurrentOpRole'
import type { OperationalPeriod } from '@/types'
import {
  SelectRoot,
  SelectTrigger,
  SelectValue,
  SelectPortal,
  SelectContent,
  SelectViewport,
  SelectItem,
  SelectItemText,
  SelectSeparator,
} from 'radix-vue'
import PermissionGuard from '@/components/ui/PermissionGuard.vue'
import FosDialog from '@/components/ui/FosDialog.vue'
import FosDateInput from '@/components/ui/FosDateInput.vue'
import FosButton from '@/components/ui/FosButton.vue'

const route = useRoute()
const opsStore = useOpsStore()
const permissionsStore = usePermissionsStore()
const toast = useToastStore()
const { opRole } = useCurrentOpRole()

const canCreateOp = computed(() => permissionsStore.can('op.create', opRole.value))

const selectOpen = ref(false)
const showNewOpDialog = ref(false)
const newOpDate = ref('')
const creating = ref(false)

function opLabel(op: OperationalPeriod): string {
  if (!op.date) return `OP ${op.op_number}`
  const [y, m, d] = op.date.split('-')
  return `OP ${op.op_number} — ${m}/${d}/${y}`
}

function openCreateDialog() {
  selectOpen.value = false
  newOpDate.value = new Date().toISOString().split('T')[0] ?? ''
  showNewOpDialog.value = true
}

async function confirmCreateOp() {
  const incidentId = route.params.id as string
  if (!incidentId || !newOpDate.value) return
  creating.value = true
  try {
    const newOpId = await opsStore.createOp(incidentId, { date: newOpDate.value })
    opsStore.selectOp(newOpId)
    showNewOpDialog.value = false
    toast.add('New operational period created', 'success')
  } catch {
    toast.add('Failed to create operational period', 'error')
  } finally {
    creating.value = false
  }
}
</script>

<template>
  <SelectRoot
    v-if="opsStore.ops.length > 0"
    v-model:open="selectOpen"
    :model-value="opsStore.selectedOpId ?? undefined"
    @update:model-value="opsStore.selectOp($event)"
  >
    <SelectTrigger
      class="flex items-center gap-1 border border-border text-outline px-2.5 py-1 rounded font-mono text-xs font-bold uppercase tracking-wider focus:outline-none transition-all shrink-0"
    >
      <SelectValue>
        <span v-if="opsStore.selectedOp">{{ opLabel(opsStore.selectedOp) }}</span>
        <span v-else class="text-outline">SELECT OP</span>
      </SelectValue>
      <span class="material-symbols-outlined text-outline text-[14px]">expand_more</span>
    </SelectTrigger>
    <SelectPortal>
      <SelectContent
        class="z-50 bg-surface-container-high border border-border rounded overflow-hidden shadow-xl min-w-[180px]"
        position="popper"
        :side-offset="4"
      >
        <SelectViewport class="p-1">
          <SelectItem
            v-for="op in opsStore.ops"
            :key="op.op_id"
            :value="op.op_id"
            class="flex items-center px-3 py-2 font-mono text-xs text-on-surface cursor-pointer focus:outline-none data-[highlighted]:bg-surface-container-highest data-[state=checked]:text-primary"
          >
            <SelectItemText>{{ opLabel(op) }}</SelectItemText>
          </SelectItem>
        </SelectViewport>
        <SelectSeparator class="h-px bg-border mx-1" />
        <div class="px-1 py-1">
          <PermissionGuard action="op.create" :role="opRole">
            <button
              class="w-full flex items-center gap-1.5 px-3 py-2 font-mono text-xs text-on-surface-variant hover:bg-surface-container-highest rounded transition-colors"
              @click="openCreateDialog"
            >
              <span class="material-symbols-outlined text-[14px]">add</span>
              New OP
            </button>
          </PermissionGuard>
        </div>
      </SelectContent>
    </SelectPortal>
  </SelectRoot>

  <FosDialog
    :open="showNewOpDialog"
    title="New Operational Period"
    @update:open="showNewOpDialog = $event"
  >
    <p class="font-mono text-xs text-on-surface-variant mb-4">
      Creates a new operational period with an auto-incremented number and a default Overhead team.
    </p>
    <div class="flex flex-col gap-1 mb-2">
      <label class="font-mono text-[0.65rem] uppercase tracking-widest text-outline">Date</label>
      <FosDateInput v-model="newOpDate" />
    </div>
    <template #footer>
      <FosButton variant="secondary" @click="showNewOpDialog = false">Cancel</FosButton>
      <FosButton :disabled="!newOpDate || creating" @click="confirmCreateOp">
        {{ creating ? 'Creating...' : 'Create OP' }}
      </FosButton>
    </template>
  </FosDialog>
</template>
